import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp, doc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { initializePaystack } from "../utils/paystack";
import { formatPrice } from "../utils/format";
import {
  NIGERIAN_STATES,
  DEFAULT_SHIPPING_SETTINGS,
  getShippingSettings,
  getPickupLocations,
  calculateShippingFee,
  getEstimatedDeliveryWindow,
} from "../utils/shipping";
import {
  ShoppingBag,
  Truck,
  MapPin,
  Clock,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";
import toast from "react-hot-toast";

export default function CheckoutPage() {
  const { cart, total, clearCart } = useCart();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=delivery/pickup, 2=review & pay
  const [fulfillmentType, setFulfillmentType] = useState("delivery"); // "delivery" | "pickup"

  // Delivery / contact state
  const [delivery, setDelivery] = useState({
    fullName: profile?.fullName || "",
    phone: profile?.phone || "",
    address: "",
    city: "",
    state: "",
  });

  // Shipping & Pickup states
  const [shippingSettings, setShippingSettings] = useState(DEFAULT_SHIPPING_SETTINGS);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [selectedPickupLocation, setSelectedPickupLocation] = useState(null);

  // Pre-fill contact details when profile finishes loading
  useEffect(() => {
    if (profile) {
      setDelivery((d) => ({
        ...d,
        fullName: d.fullName || profile.fullName || "",
        phone: d.phone || profile.phone || "",
      }));
    }
  }, [profile]);

  // Load shipping settings & active pickup locations
  useEffect(() => {
    async function initShipping() {
      try {
        const [settingsData, locationsData] = await Promise.all([
          getShippingSettings(),
          getPickupLocations(null, true), // only active
        ]);
        setShippingSettings(settingsData);
        setPickupLocations(locationsData);
      } catch (err) {
        console.warn("Could not load shipping config:", err);
      }
    }
    initShipping();
  }, []);

  // Filter pickup locations when user changes state in pickup mode
  const availablePickupLocations = pickupLocations.filter(
    (loc) => loc.state?.toLowerCase() === delivery.state?.toLowerCase() && loc.active
  );

  // Auto-select first pickup location if only 1 exists, or reset if state changed
  useEffect(() => {
    if (fulfillmentType === "pickup") {
      const matching = pickupLocations.filter(
        (loc) => loc.state?.toLowerCase() === delivery.state?.toLowerCase() && loc.active
      );
      if (matching.length === 1) {
        setSelectedPickupLocation(matching[0]);
      } else if (
        selectedPickupLocation &&
        selectedPickupLocation.state?.toLowerCase() !== delivery.state?.toLowerCase()
      ) {
        setSelectedPickupLocation(null);
      }
    }
  }, [delivery.state, fulfillmentType, pickupLocations, selectedPickupLocation]);

  // Support coupon passed from CartPage
  const coupon = location.state?.coupon;
  const discount = coupon?.valid ? coupon.discount : 0;

  // Calculate live dynamic shipping fee
  const shippingCalc = calculateShippingFee({
    fulfillmentType,
    state: delivery.state,
    cart,
    subtotal: total,
    settings: shippingSettings,
  });

  const shippingFee = shippingCalc.fee;
  const grandTotal = Math.max(0, total - discount + shippingFee);

  // 7-day delivery timeline guarantee
  const deliveryWindow = getEstimatedDeliveryWindow(
    new Date(),
    shippingSettings.deliveryDays || 7
  );

  const set = (k) => (e) => setDelivery((d) => ({ ...d, [k]: e.target.value }));

  // Proceed from Step 1 to Step 2
  function handleContinueToReview() {
    if (!delivery.fullName.trim()) {
      return toast.error("Please enter your full name.");
    }
    if (!delivery.phone.trim()) {
      return toast.error("Please enter your phone number.");
    }

    if (fulfillmentType === "delivery") {
      if (!delivery.address.trim() || !delivery.city.trim() || !delivery.state) {
        return toast.error("Please fill in your delivery street address, city, and state.");
      }
    } else {
      // Pickup mode
      if (!delivery.state) {
        return toast.error("Please select a state to find nearby pickup stations.");
      }
      if (availablePickupLocations.length === 0) {
        return toast.error(
          `No pickup stations are currently available in ${delivery.state}. Please choose Door Delivery or select another state.`
        );
      }
      if (!selectedPickupLocation) {
        return toast.error("Please select a pickup station to collect your order.");
      }
    }

    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Handle Paystack Payment
  async function handlePayment() {
    if (!user) {
      toast.error("Please log in to complete your checkout.");
      navigate("/login", { state: { from: "/checkout" } });
      return;
    }

    if (fulfillmentType === "delivery") {
      if (!delivery.fullName || !delivery.phone || !delivery.address || !delivery.city || !delivery.state) {
        toast.error("Please fill in all delivery details before paying.");
        setStep(1);
        return;
      }
    } else {
      if (!delivery.fullName || !delivery.phone || !delivery.state || !selectedPickupLocation) {
        toast.error("Please select your state and pickup station before paying.");
        setStep(1);
        return;
      }
    }

    if (cart.length === 0) {
      toast.error("Your cart is empty.");
      navigate("/shop");
      return;
    }

    setLoading(true);

    try {
      await initializePaystack({
        email: user.email,
        amount: grandTotal,
        metadata: {
          userId: user.uid,
          customerName: delivery.fullName || profile?.fullName || "",
          customerPhone: delivery.phone || "",
          fulfillmentType,
          pickupLocationId: selectedPickupLocation?.id || null,
          pickupStationName: selectedPickupLocation?.name || null,
          estimatedDelivery: deliveryWindow.text,
          items: cart.map((i) => ({ id: i.id, name: i.name, qty: i.qty, price: i.price })),
        },
        onSuccess: async (response) => {
          try {
            // Build delivery/pickup information payload
            const orderDeliveryData =
              fulfillmentType === "delivery"
                ? {
                    fullName: delivery.fullName,
                    phone: delivery.phone,
                    address: delivery.address,
                    city: delivery.city,
                    state: delivery.state,
                  }
                : {
                    fullName: delivery.fullName,
                    phone: delivery.phone,
                    state: delivery.state,
                    pickupLocationId: selectedPickupLocation.id,
                    pickupLocationName: selectedPickupLocation.name,
                    pickupAddress: selectedPickupLocation.address,
                    pickupCity: selectedPickupLocation.city,
                    pickupPhone: selectedPickupLocation.phone || "",
                    pickupHours: selectedPickupLocation.operatingHours || "",
                    pickupNotes: selectedPickupLocation.notes || "",
                  };

            // Save order to Firestore
            const orderDoc = await addDoc(collection(db, "orders"), {
              userId: user.uid,
              userEmail: user.email,
              userName: delivery.fullName || profile?.fullName || "",
              items: cart,
              subtotal: total,
              discount,
              couponCode: coupon?.coupon?.code || null,
              shipping: shippingFee,
              total: grandTotal,
              fulfillmentType, // "delivery" | "pickup"
              delivery: orderDeliveryData,
              pickupLocation: fulfillmentType === "pickup" ? selectedPickupLocation : null,
              deliveryTimeline: `Within ${shippingSettings.deliveryDays || 7} days of payment`,
              estimatedDeliveryDate: deliveryWindow.dateText,
              status: "confirmed",
              paymentRef: response.reference || response.trxref || `REF-${Date.now()}`,
              paymentStatus: "paid",
              createdAt: serverTimestamp(),
            });

            // Safely decrement stock for each item
            for (const item of cart) {
              try {
                await updateDoc(doc(db, "products", item.id), {
                  stock: increment(-item.qty),
                });
              } catch (stockErr) {
                console.warn(`Stock decrement skipped for ${item.id}:`, stockErr?.message);
              }
            }

            clearCart();
            toast.success("Order placed successfully! 🎉");
            navigate(`/order-success/${orderDoc.id}`);
          } catch (orderSaveErr) {
            console.error("Order creation error:", orderSaveErr);
            toast.error(
              `Payment succeeded (Ref: ${
                response.reference || response.trxref
              }), but saving order had an issue. Please contact Afriport support.`
            );
          } finally {
            setLoading(false);
          }
        },
        onClose: () => {
          setLoading(false);
          toast("Payment cancelled or modal closed.");
        },
      });
    } catch (err) {
      console.error("Payment initialization error:", err);
      toast.error(err?.message || "Payment initialization failed. Please check your connection.");
      setLoading(false);
    }
  }

  if (cart.length === 0) {
    return (
      <div
        style={{
          background: "var(--surface)",
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: 24,
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            background: "var(--brand-light)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ShoppingBag size={36} color="var(--brand)" />
        </div>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 700 }}>
          Your cart is empty
        </h2>
        <p style={{ color: "var(--text-muted)" }}>
          Add some items from our shop before checking out.
        </p>
        <Link to="/shop" className="btn btn-primary">
          Browse Shop
        </Link>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh", paddingBottom: 60 }}>
      <div className="container section-sm" style={{ maxWidth: 940 }}>
        {/* Title & Delivery Guarantee Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800 }}>
              Checkout
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4 }}>
              Choose fulfillment method and complete your order securely.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              background: "#ECFDF5",
              border: "1px solid #A7F3D0",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600,
              color: "#065F46",
            }}
          >
            <Clock size={16} color="var(--success)" />
            <span>Guaranteed Delivery within 7 Days</span>
          </div>
        </div>

        {/* Progress Steps */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
          {[
            ["1", "Fulfillment & Details"],
            ["2", "Review & Pay"],
          ].map(([n, label], i) => (
            <React.Fragment key={n}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: step >= Number(n) ? "var(--brand)" : "var(--border)",
                    color: step >= Number(n) ? "#fff" : "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {n}
                </div>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: step === Number(n) ? 600 : 400,
                    color: step === Number(n) ? "var(--text)" : "var(--text-muted)",
                  }}
                >
                  {label}
                </span>
              </div>
              {i < 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: step > Number(n) ? "var(--brand)" : "var(--border)",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="checkout-layout-grid">
          {/* Main Form Column */}
          <div>
            {step === 1 && (
              <div
                className="card"
                style={{
                  padding: "clamp(20px, 4vw, 32px)",
                  background: "#fff",
                  border: "1px solid var(--border)",
                  boxShadow: "var(--shadow-sm)",
                }}
              >
                {/* Fulfillment Type Toggle */}
                <div style={{ marginBottom: 28 }}>
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      display: "block",
                      marginBottom: 12,
                    }}
                  >
                    Select Fulfillment Method *
                  </label>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                      gap: 14,
                    }}
                  >
                    {/* Door Delivery Option */}
                    <div
                      onClick={() => setFulfillmentType("delivery")}
                      style={{
                        padding: 16,
                        borderRadius: 12,
                        border:
                          fulfillmentType === "delivery"
                            ? "2px solid var(--brand)"
                            : "1.5px solid var(--border)",
                        background:
                          fulfillmentType === "delivery"
                            ? "rgba(232, 71, 26, 0.04)"
                            : "var(--surface)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        transition: "all 0.2s",
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background:
                            fulfillmentType === "delivery" ? "var(--brand)" : "#E2E8F0",
                          color: fulfillmentType === "delivery" ? "#fff" : "var(--text-muted)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Truck size={20} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color:
                              fulfillmentType === "delivery" ? "var(--brand)" : "var(--text)",
                          }}
                        >
                          Door Delivery
                        </div>
                        <p
                          style={{
                            fontSize: 12,
                            color: "var(--text-muted)",
                            margin: "4px 0 0",
                            lineHeight: 1.4,
                          }}
                        >
                          Delivered directly to your home or office address in any state.
                        </p>
                      </div>
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          border:
                            fulfillmentType === "delivery"
                              ? "5px solid var(--brand)"
                              : "2px solid var(--border)",
                          background: "#fff",
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      />
                    </div>

                    {/* Station Pickup Option */}
                    <div
                      onClick={() => setFulfillmentType("pickup")}
                      style={{
                        padding: 16,
                        borderRadius: 12,
                        border:
                          fulfillmentType === "pickup"
                            ? "2px solid var(--brand)"
                            : "1.5px solid var(--border)",
                        background:
                          fulfillmentType === "pickup"
                            ? "rgba(232, 71, 26, 0.04)"
                            : "var(--surface)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        transition: "all 0.2s",
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background:
                            fulfillmentType === "pickup" ? "var(--brand)" : "#E2E8F0",
                          color: fulfillmentType === "pickup" ? "#fff" : "var(--text-muted)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <MapPin size={20} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color:
                              fulfillmentType === "pickup" ? "var(--brand)" : "var(--text)",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          Station Pickup
                          <span
                            className="badge badge-success"
                            style={{ fontSize: 10, padding: "2px 6px" }}
                          >
                            Free / Reduced
                          </span>
                        </div>
                        <p
                          style={{
                            fontSize: 12,
                            color: "var(--text-muted)",
                            margin: "4px 0 0",
                            lineHeight: 1.4,
                          }}
                        >
                          Pick up from a verified Afriport station hub in your state.
                        </p>
                      </div>
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          border:
                            fulfillmentType === "pickup"
                              ? "5px solid var(--brand)"
                              : "2px solid var(--border)",
                          background: "#fff",
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* ⏱️ 7-Day Timeline Notice */}
                <div
                  style={{
                    padding: "12px 16px",
                    background: "rgba(232, 71, 26, 0.05)",
                    border: "1px solid rgba(232, 71, 26, 0.15)",
                    borderRadius: 10,
                    marginBottom: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <Clock size={20} color="var(--brand)" style={{ flexShrink: 0 }} />
                  <p style={{ fontSize: 13, color: "var(--text)", margin: 0, lineHeight: 1.5 }}>
                    <strong>7-Day Delivery Guarantee:</strong> All orders are delivered or ready for
                    pickup within <strong>7 days</strong> from payment confirmation (by{" "}
                    <strong>{deliveryWindow.dateText}</strong>).
                  </p>
                </div>

                {/* Contact Information */}
                <h3
                  style={{
                    fontFamily: "var(--font-head)",
                    fontWeight: 700,
                    fontSize: 16,
                    marginBottom: 16,
                  }}
                >
                  {fulfillmentType === "delivery" ? "Recipient Details" : "Pickup Contact Details"}
                </h3>

                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  <div className="responsive-two-col">
                    <div className="form-group">
                      <label>Full Name *</label>
                      <input
                        className="form-input"
                        required
                        placeholder="First and last name"
                        value={delivery.fullName}
                        onChange={set("fullName")}
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input
                        className="form-input"
                        required
                        placeholder="08012345678"
                        value={delivery.phone}
                        onChange={set("phone")}
                      />
                    </div>
                  </div>

                  {/* 🚚 DOOR DELIVERY SPECIFIC FIELDS */}
                  {fulfillmentType === "delivery" && (
                    <>
                      <div className="form-group">
                        <label>Delivery Address *</label>
                        <input
                          className="form-input"
                          placeholder="House/flat number, street name, nearest landmark"
                          required
                          value={delivery.address}
                          onChange={set("address")}
                        />
                      </div>

                      <div className="responsive-two-col">
                        <div className="form-group">
                          <label>City / LGA *</label>
                          <input
                            className="form-input"
                            placeholder="e.g. Ikeja, Wuse 2, Port Harcourt"
                            required
                            value={delivery.city}
                            onChange={set("city")}
                          />
                        </div>
                        <div className="form-group">
                          <label>State *</label>
                          <select
                            className="form-select"
                            required
                            value={delivery.state}
                            onChange={set("state")}
                          >
                            <option value="">Select delivery state</option>
                            {NIGERIAN_STATES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}

                  {/* 🏪 STATION PICKUP SPECIFIC FIELDS */}
                  {fulfillmentType === "pickup" && (
                    <div
                      style={{
                        padding: 20,
                        background: "var(--surface)",
                        borderRadius: 12,
                        border: "1px solid var(--border)",
                      }}
                    >
                      <div className="form-group" style={{ marginBottom: 16 }}>
                        <label style={{ fontWeight: 700 }}>
                          Select Your State to Find Pickup Locations *
                        </label>
                        <select
                          className="form-select"
                          required
                          value={delivery.state}
                          onChange={set("state")}
                          style={{
                            padding: "12px 16px",
                            borderRadius: 8,
                            fontSize: 15,
                            marginTop: 6,
                          }}
                        >
                          <option value="">-- Choose your state for pickup --</option>
                          {NIGERIAN_STATES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* When state is selected, show pickup locations for that zone */}
                      {delivery.state && (
                        <div>
                          {availablePickupLocations.length > 0 ? (
                            <div>
                              <p
                                style={{
                                  fontSize: 13,
                                  fontWeight: 700,
                                  color: "var(--text-muted)",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  marginBottom: 10,
                                }}
                              >
                                Available Pickup Stations in {delivery.state} (
                                {availablePickupLocations.length})
                              </p>

                              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {availablePickupLocations.map((loc) => {
                                  const isSelected = selectedPickupLocation?.id === loc.id;
                                  return (
                                    <div
                                      key={loc.id}
                                      onClick={() => setSelectedPickupLocation(loc)}
                                      style={{
                                        padding: 14,
                                        borderRadius: 10,
                                        border: isSelected
                                          ? "2px solid var(--brand)"
                                          : "1px solid var(--border)",
                                        background: isSelected
                                          ? "rgba(232, 71, 26, 0.04)"
                                          : "#fff",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 12,
                                        transition: "all 0.15s",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: 20,
                                          height: 20,
                                          borderRadius: "50%",
                                          border: isSelected
                                            ? "6px solid var(--brand)"
                                            : "2px solid var(--border)",
                                          background: "#fff",
                                          flexShrink: 0,
                                          marginTop: 2,
                                        }}
                                      />
                                      <div style={{ flex: 1 }}>
                                        <div
                                          style={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            color: isSelected ? "var(--brand)" : "var(--text)",
                                            marginBottom: 4,
                                          }}
                                        >
                                          {loc.name}
                                        </div>
                                        <p
                                          style={{
                                            fontSize: 13,
                                            color: "#475569",
                                            lineHeight: 1.4,
                                            margin: "0 0 6px",
                                          }}
                                        >
                                          {loc.address}, {loc.city}
                                        </p>
                                        <div
                                          style={{
                                            fontSize: 12,
                                            color: "var(--text-muted)",
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 12,
                                          }}
                                        >
                                          {loc.phone && <span>📞 {loc.phone}</span>}
                                          <span>
                                            🕒 {loc.operatingHours || "Mon-Sat: 9am - 6pm"}
                                          </span>
                                        </div>
                                        {loc.notes && (
                                          <div
                                            style={{
                                              fontSize: 11,
                                              color: "var(--text-muted)",
                                              marginTop: 4,
                                              fontStyle: "italic",
                                            }}
                                          >
                                            ℹ️ {loc.notes}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                padding: 18,
                                background: "#FFFBEB",
                                border: "1px solid #FDE68A",
                                borderRadius: 10,
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 12,
                              }}
                            >
                              <AlertCircle
                                size={20}
                                color="#D97706"
                                style={{ flexShrink: 0, marginTop: 2 }}
                              />
                              <div>
                                <p
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: "#92400E",
                                    margin: 0,
                                  }}
                                >
                                  No Pickup Stations Currently in {delivery.state}
                                </p>
                                <p
                                  style={{
                                    fontSize: 13,
                                    color: "#B45309",
                                    margin: "4px 0 10px",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  We do not have a station hub operating in {delivery.state} yet.
                                  Please switch to <strong>Door Delivery</strong> to have your
                                  package delivered directly to your doorstep.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setFulfillmentType("delivery")}
                                  className="btn btn-secondary btn-sm"
                                  style={{ background: "#fff" }}
                                >
                                  Switch to Door Delivery
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    className="btn btn-primary btn-lg"
                    style={{ marginTop: 12 }}
                    onClick={handleContinueToReview}
                  >
                    Continue to Review & Pay
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: REVIEW & CONFIRM */}
            {step === 2 && (
              <div>
                {/* Fulfillment Details Review Card */}
                <div
                  className="card"
                  style={{
                    padding: 24,
                    marginBottom: 16,
                    background: "#fff",
                    border: "1px solid var(--border)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {fulfillmentType === "delivery" ? (
                        <Truck size={18} color="var(--brand)" />
                      ) : (
                        <MapPin size={18} color="var(--brand)" />
                      )}
                      <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 16 }}>
                        {fulfillmentType === "delivery"
                          ? "Door Delivery Details"
                          : "Station Pickup Details"}
                      </h3>
                    </div>
                    <button
                      onClick={() => setStep(1)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "var(--brand)",
                        fontSize: 13,
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Edit
                    </button>
                  </div>

                  {fulfillmentType === "delivery" ? (
                    <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>
                        {delivery.fullName} · {delivery.phone}
                      </p>
                      <p style={{ margin: 0, color: "#475569" }}>
                        {delivery.address}, {delivery.city}, {delivery.state} State
                      </p>
                      <div
                        style={{
                          marginTop: 10,
                          fontSize: 13,
                          color: "var(--success)",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <Clock size={15} /> Expected delivery: {deliveryWindow.text}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 14, lineHeight: 1.7 }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>
                        Collector: {delivery.fullName} · {delivery.phone}
                      </p>
                      <div
                        style={{
                          marginTop: 8,
                          padding: 12,
                          background: "#F8FAFC",
                          borderRadius: 8,
                          border: "1px solid var(--border)",
                        }}
                      >
                        <p style={{ margin: 0, fontWeight: 700, color: "var(--brand)" }}>
                          🏪 {selectedPickupLocation?.name}
                        </p>
                        <p style={{ margin: "2px 0 0", color: "#475569", fontSize: 13 }}>
                          {selectedPickupLocation?.address}, {selectedPickupLocation?.city},{" "}
                          {selectedPickupLocation?.state} State
                        </p>
                        <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 12 }}>
                          Hours: {selectedPickupLocation?.operatingHours || "Mon-Sat: 9am - 6pm"}
                          {selectedPickupLocation?.phone ? ` · Tel: ${selectedPickupLocation.phone}` : ""}
                        </p>
                      </div>
                      <div
                        style={{
                          marginTop: 10,
                          fontSize: 13,
                          color: "var(--success)",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <Clock size={15} /> Ready for pickup: {deliveryWindow.text}
                      </div>
                    </div>
                  )}
                </div>

                {/* Items Review Card */}
                <div
                  className="card"
                  style={{
                    padding: 24,
                    background: "#fff",
                    border: "1px solid var(--border)",
                  }}
                >
                  <h3
                    style={{
                      fontFamily: "var(--font-head)",
                      fontWeight: 700,
                      fontSize: 16,
                      marginBottom: 16,
                    }}
                  >
                    Order Items ({cart.length})
                  </h3>
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "center",
                        padding: "12px 0",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "var(--surface)",
                          flexShrink: 0,
                        }}
                      >
                        {item.image ? (
                          <img
                            src={item.image}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            📦
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{item.name}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          Qty: {item.qty} · {formatPrice(item.price)} each
                          {item.shippingType === "free" && (
                            <span
                              style={{
                                marginLeft: 8,
                                color: "var(--success)",
                                fontWeight: 600,
                              }}
                            >
                              Free Shipping
                            </span>
                          )}
                          {item.shippingType === "surcharge" && item.shippingSurcharge > 0 && (
                            <span
                              style={{
                                marginLeft: 8,
                                color: "#D97706",
                                fontWeight: 500,
                              }}
                            >
                              Heavy Surcharge: +{formatPrice(item.shippingSurcharge)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {formatPrice(item.price * item.qty)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Summary Sidebar */}
          <div
            className="card"
            style={{
              padding: 24,
              position: "sticky",
              top: 90,
              background: "#fff",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <h3
              style={{
                fontFamily: "var(--font-head)",
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 16,
              }}
            >
              Order & Payment Summary
            </h3>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {/* Subtotal */}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span style={{ color: "var(--text-muted)" }}>Subtotal</span>
                <span>{formatPrice(total)}</span>
              </div>

              {/* Discount */}
              {discount > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span style={{ color: "var(--success)" }}>Coupon Discount</span>
                  <span style={{ fontWeight: 600, color: "var(--success)" }}>
                    -{formatPrice(discount)}
                  </span>
                </div>
              )}

              {/* Shipping / Pickup Fee */}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                <span style={{ color: "var(--text-muted)" }}>
                  {fulfillmentType === "pickup" ? "Station Pickup" : "Delivery Fee"}
                </span>
                <span
                  style={{
                    color: shippingFee === 0 ? "var(--success)" : "inherit",
                    fontWeight: 600,
                  }}
                >
                  {shippingFee === 0 ? "Free 🎉" : formatPrice(shippingFee)}
                </span>
              </div>

              {/* Breakdown Note */}
              {shippingCalc.breakdown && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    background: "var(--surface)",
                    padding: "6px 10px",
                    borderRadius: 6,
                  }}
                >
                  ℹ️ {shippingCalc.breakdown}
                </div>
              )}
            </div>

            {/* Total */}
            <div
              style={{
                borderTop: "1px solid var(--border)",
                paddingTop: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 16 }}>Total</span>
              <span
                style={{
                  fontFamily: "var(--font-head)",
                  fontWeight: 800,
                  fontSize: 20,
                  color: "var(--brand)",
                }}
              >
                {formatPrice(grandTotal)}
              </span>
            </div>

            {/* Pay Button */}
            {step === 2 ? (
              <button
                onClick={handlePayment}
                disabled={loading}
                className="btn btn-primary btn-lg w-full"
                style={{ fontSize: 16 }}
              >
                {loading ? "Processing Payment…" : `Pay ${formatPrice(grandTotal)} with Paystack`}
              </button>
            ) : (
              <button
                onClick={handleContinueToReview}
                className="btn btn-primary btn-lg w-full"
                style={{ fontSize: 15 }}
              >
                Review Order Details →
              </button>
            )}

            {/* 7-Day Guarantee Assurance Box */}
            <div
              style={{
                marginTop: 20,
                padding: "12px 14px",
                background: "#F8FAFC",
                border: "1px solid var(--border)",
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--text)",
                }}
              >
                <ShieldCheck size={16} color="var(--brand)" />
                <span>Delivery Guarantee</span>
              </div>
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0", lineHeight: 1.4 }}>
                Delivery is guaranteed within <strong>7 days</strong> from payment confirmation.
              </p>
            </div>

            <p
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                textAlign: "center",
                marginTop: 12,
              }}
            >
              🔒 100% Secure Checkout powered by Paystack
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
